import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchInvoiceEditData } from '../v2/features/customer/customer.slice';
import { getTimesheetWorkingHours } from '../v2/features/timesheet/timesheet.slice';
import '../v2/pages/timesheet/timeSheetsPage/timeSheetsPage.scss';

interface InvoiceByTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: InvoiceByTimesheetData) => void;
}

export interface InvoiceByTimesheetData {
  clientId: string;
  fromDate: string;
  toDate: string;
}

const InvoiceByTimesheetModal: React.FC<InvoiceByTimesheetModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get user ID from Redux auth state
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  
  // Get invoice edit data (including customers) from Redux customer state
  const { invoiceEditData, loading } = useSelector((state: any) => state.customer);
  
  // Extract customers from invoiceEditData
  const customers = invoiceEditData?.response?.customers || [];

  const [clientId, setClientId] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [timesheetResponseData, setTimesheetResponseData] = useState<any>(null);
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredBadge, setHoveredBadge] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const clientDropdownRef = React.useRef<HTMLDivElement>(null);

  // Validation error states
  const [clientError, setClientError] = useState<string>('');
  const [fromDateError, setFromDateError] = useState<string>('');
  const [toDateError, setToDateError] = useState<string>('');

  const taxFreeArticleBadges = useMemo(() => {
    if (!timesheetResponseData) return [] as Array<{ articleNumber: string; label: string; workingDates: string[]; tripStartedDate: string; tripEndedDate: string }>;

    const response = timesheetResponseData?.response;
    const list = Array.isArray(response?.timeSheetWorkingHoursV2List)
      ? response.timeSheetWorkingHoursV2List
      : Array.isArray(response)
        ? response
        : response
          ? [response]
          : [];

    const getArticleLabel = (articleNumber: string, fallbackDescription?: string) => {
      switch (articleNumber) {
        case '93':
          return t('MILEAGE_REPORT') || 'Mileage Report';
        case '101':
          return t('ALLOWANCE_IN_SWEDEN') || 'Allowance In Sweden';
        case '100':
          return t('ALLOWANCE_OUTSIDE_SWEDEN') || 'Allowance Outside Sweden';
        case '98':
        case '91':
          return t('TRAVEL_EXPENSES') || 'Travel Expenses';
        case '99':
          return t('MATERIALS_AND_TOOLS') || 'Materials & Tools';
        default:
          return fallbackDescription || `${t('ARTICLE') || 'Article'} ${articleNumber}`;
      }
    };

    const unique = new Map<string, { articleNumber: string; label: string; workingDates: string[]; tripStartedDate: string; tripEndedDate: string }>();

    list.forEach((item: any) => {
      if (!item?.isTaxFreeArticle) return;
      const articleNumber = String(item?.articleNumber || '').trim();
      if (!articleNumber) return;
      const workingDate = item?.workingDate ? String(item.workingDate) : '';
      const tripStart = item?.tripStartedDate ? String(item.tripStartedDate) : '';
      const tripEnd = item?.tripEndedDate ? String(item.tripEndedDate) : '';

      if (!unique.has(articleNumber)) {
        unique.set(articleNumber, {
          articleNumber,
          label: getArticleLabel(articleNumber, item?.workingDescription),
          workingDates: workingDate ? [workingDate] : [],
          tripStartedDate: tripStart,
          tripEndedDate: tripEnd,
        });
      } else {
        const existing = unique.get(articleNumber)!;
        if (workingDate && !existing.workingDates.includes(workingDate)) {
          existing.workingDates.push(workingDate);
        }
        if (tripStart && (!existing.tripStartedDate || tripStart < existing.tripStartedDate)) {
          existing.tripStartedDate = tripStart;
        }
        if (tripEnd && (!existing.tripEndedDate || tripEnd > existing.tripEndedDate)) {
          existing.tripEndedDate = tripEnd;
        }
      }
    });

    return Array.from(unique.values());
  }, [timesheetResponseData, t]);

  // Fetch customer data when component mounts or userId changes
  useEffect(() => {
    if (userId && !invoiceEditData) {
      dispatch(fetchInvoiceEditData(userId) as any);
    }
  }, [dispatch, userId, invoiceEditData]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
    };

    if (isClientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClientDropdownOpen]);

  // Auto-fetch timesheet working hours when date range is selected, but only if client is selected
  useEffect(() => {
    const fetchWorkingHoursOnDateSelection = async () => {
      if (!isOpen) return;
      if (!fromDate || !toDate) return;

      // Also validate client before API call
      if (!clientId || clientId.trim() === '') {
        setClientError(t('CLIENT_REQUIRED') || 'Client is required');
        return;
      }

      // Clear stale client error if client is selected
      setClientError('');

      try {
        const result = await dispatch(getTimesheetWorkingHours({
          fromDate: fromDate || '',
          toDate: toDate || '',
          Month: '',
          year: '',
          customerId: clientId || ''
        }) as any).unwrap();

        setTimesheetResponseData(result);
      } catch (error) {
        console.error('Failed to auto-fetch timesheet working hours:', error);
      }
    };

    fetchWorkingHoursOnDateSelection();
  }, [isOpen, fromDate, toDate, clientId, dispatch, t]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    // Clear previous errors
    setClientError('');
    setFromDateError('');
    setToDateError('');
    
    // Validate required fields
    let hasError = false;
    
    if (!clientId || clientId.trim() === '') {
      setClientError(t('CLIENT_REQUIRED') || 'Client is required');
      hasError = true;
    }
    
    if (!fromDate || fromDate.trim() === '') {
      setFromDateError(t('FROM_DATE_REQUIRED') || 'From date is required');
      hasError = true;
    }
    
    if (!toDate || toDate.trim() === '') {
      setToDateError(t('TO_DATE_REQUIRED') || 'To date is required');
      hasError = true;
    }
    
    // If any error exists, stop submission
    if (hasError) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Call the GET API to fetch timesheet data
      const result = await dispatch(getTimesheetWorkingHours({
        fromDate: fromDate || '',
        toDate: toDate || '',
        Month: '',
        year: '',
        customerId: clientId || ''
      }) as any).unwrap();

      // Check if timeSheetWorkingHoursV2List is empty
      const workingHoursList = result?.response?.timeSheetWorkingHoursV2List || [];
      
      if (!workingHoursList || workingHoursList.length === 0) {
        // Show error toast if no working hours found
        toast.error(String(t('NO_TIMESHEET_WORKING_HOURS_FOUND') || 'No timesheet working hours found. Please create and save timesheet working hours first, then create invoice.'));
        return; // Don't redirect or close modal
      }
      
      // Save response to state
      setTimesheetResponseData(result);

      // Call the parent onSubmit callback
      onSubmit({
        clientId,
        fromDate,
        toDate,
      });

      // Redirect based on current language
      const currentLanguage = i18n.language;
      const langParam = currentLanguage === 'sv' || currentLanguage === 'sv-SE' ? 'sv-se' : 'en-us';
      navigate(`/v2/create-invoice?lang=${langParam}&timesheet=true`);
      
      // Close modal after successful submission
      onClose();
    } catch (error) {
      console.error('Failed to fetch timesheet data:', error);
      // Keep modal open on error so user can retry
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="invoice-timesheet-modal">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title" style={{color: '#000',
fontFamily: "Plus Jakarta Sans",
fontSize: '16px',
fontStyle: 'normal',
fontWeight: '600',
lineHeight: 'normal',
letterSpacing: '-0.16px',}}>
            {t('CREATE_INVOICE_BY_TIMESHEET') || 'Create invoice by timesheet'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#000000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="modal-description" style={{color: '#000',
fontFamily: "Plus Jakarta Sans",
fontSize: '15px',
fontStyle: 'normal',
fontWeight: '400',
lineHeight: '22px', /* 146.667% */}}>
            {t('INVOICE_MODAL_DESCRIPTION') ||
              'Please enter the below details and it will update your invoice accordingly.'}
          </p>

          {/* Client Details Field */}
          <div className="modal-field">
            <label className="modal-field-label">
              {t('CLIENT_DETAILS') || 'Client Details'}
            </label>
            <div className="modal-input-wrapper custom-dropdown" ref={clientDropdownRef}>
              <div 
                className={`dropdown-trigger ${isClientDropdownOpen ? 'open' : ''} ${clientError ? 'error' : ''}`}
                onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                style={{
                  border: clientError ? '1px solid #DC3545' : '',
                  boxShadow: clientError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : ''
                }}
              >
                <span className="dropdown-value">
                  {clientId && customers.length > 0
                    ? customers.find((c: any) => String(c.customerId || c.id) === String(clientId))?.name || t('SELECT_CLIENT')
                    : t('SELECT_CLIENT')}
                </span>
                <svg className={`dropdown-arrow ${isClientDropdownOpen ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path 
                    d="M5 7.5L10 12.5L15 7.5" 
                    stroke="#68727D" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isClientDropdownOpen && (
                <div className="dropdown-list">
                  <div className="dropdown-content">
                    {loading ? (
                      <div className="list-item no-results">
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t('LOADING') || 'Loading...'}</p>
                          </div>
                        </div>
                      </div>
                    ) : customers.length > 0 ? (
                      customers.map((customer: any) => (
                        <div
                          key={customer.customerId || customer.id}
                          className={`list-item ${String(clientId) === String(customer.customerId || customer.id) ? 'selected' : ''}`}
                          onClick={() => {
                            setClientId(String(customer.customerId || customer.id));
                            setClientError(''); // Clear error when client selected
                            setIsClientDropdownOpen(false);
                          }}
                        >
                          <div className="text-input">
                            <div className="paragraph-container">
                              <p className="option-text">{customer.name}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="list-item no-results">
                        <div className="text-input">
                          <div className="paragraph-container">
                            <p className="option-text">{t('NO_CLIENTS_FOUND')}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            {clientError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {clientError}
              </p>
            )}
          </div>

         
          <div className="modal-field">
            <label className="modal-field-label">
              {t('FROM_DATE') || 'From Date'}
            </label>
            <div className="modal-input-wrapper">
              <input
                type="date"
                className="modal-input"
                placeholder="dd/mm/yyyy"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setFromDateError(''); // Clear error when date selected
                }}
                style={{
                  border: fromDateError ? '1px solid #DC3545' : '1px solid #EAEBF0',
                  boxShadow: fromDateError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                }}
              />
            </div>
            {fromDateError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {fromDateError}
              </p>
            )}
          </div>

          {/* To Date Field */}
          <div className="modal-field">
            <label className="modal-field-label">
              {t('TO_DATE') || 'To Date'}
            </label>
            <div className="modal-input-wrapper">
              <input
                type="date"
                className="modal-input"
                placeholder="dd/mm/yyyy"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setToDateError(''); // Clear error when date selected
                }}
                style={{
                  border: toDateError ? '1px solid #DC3545' : '1px solid #EAEBF0',
                  boxShadow: toDateError ? '0 0 0 2px rgba(220, 53, 69, 0.1)' : 'none'
                }}
              />
            </div>
            {toDateError && (
              <p style={{
                color: '#DC3545',
                fontSize: '12px',
                marginTop: '4px',
                marginBottom: '0'
              }}>
                {toDateError}
              </p>
            )}

              <div style={{ marginTop: '8px' }}>
                {/* <p style={{
                  margin: '0 0 6px 0',
                  color: '#344054',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Plus Jakarta Sans'
                }}>
                  {t('TAX_FREE_ARTICLE') || 'Tax Free Article'}
                </p> */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {taxFreeArticleBadges.length === 0 && 
                    <h5 className="modal-field-label">{t('THE_LIST_OF_ALL_THE_DEDUCTIONS')}</h5>
                  }
                  {taxFreeArticleBadges.map((badge) => {
                    const formatDate = (iso: string) => {
                      const d = new Date(iso);
                      if (isNaN(d.getTime())) return iso;
                      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
                    };

                    let tooltipText: string;
                    if (badge.tripStartedDate || badge.tripEndedDate) {
                      const startFmt = badge.tripStartedDate ? formatDate(badge.tripStartedDate) : '';
                      const endFmt = badge.tripEndedDate ? formatDate(badge.tripEndedDate) : '';
                      const dateStr = startFmt && endFmt && startFmt !== endFmt
                        ? `${startFmt} - ${endFmt}`
                        : startFmt || endFmt;
                      tooltipText = `Deductions of : ${dateStr}`;
                    } else {
                      const sorted = [...badge.workingDates].sort();
                      const minDate = sorted.length > 0 ? formatDate(sorted[0]) : '';
                      const maxDate = sorted.length > 0 ? formatDate(sorted[sorted.length - 1]) : '';
                      const dateStr = minDate && maxDate && minDate !== maxDate
                        ? `${minDate} - ${maxDate}`
                        : minDate || maxDate;
                      tooltipText = dateStr ? `Deductions of : ${dateStr}` : 'Deductions';
                    }

                    return (
                      <div
                        key={badge.articleNumber}
                        style={{ position: 'relative', display: 'inline-flex' }}
                        onMouseEnter={(e) => {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.top - 8,
                            left: rect.left + rect.width / 2,
                          });
                          setHoveredBadge(badge.articleNumber);
                        }}
                        onMouseLeave={() => {
                          setHoveredBadge(null);
                          setTooltipPosition(null);
                        }}
                      >
                        {hoveredBadge === badge.articleNumber && tooltipPosition && (
                          <div style={{
                            position: 'fixed',
                            top: tooltipPosition.top,
                            left: tooltipPosition.left,
                            transform: 'translate(-50%, -100%)',
                            backgroundColor: '#1D2939',
                            color: '#fff',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 400,
                            fontFamily: 'Plus Jakarta Sans',
                            lineHeight: '18px',
                            whiteSpace: 'nowrap',
                            zIndex: 9999,
                            pointerEvents: 'none',
                          }}>
                            {tooltipText}
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              borderWidth: '5px',
                              borderStyle: 'solid',
                              borderColor: '#1D2939 transparent transparent transparent',
                            }} />
                          </div>
                        )}
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: '1px solid #D8D5EA',
                            backgroundColor: '#F4F3F9',
                            color: '#000',
                            fontSize: '16px',
                            fontWeight: 400,
                            fontStyle: 'normal',
                            lineHeight: '140%',
                            fontFamily: 'Plus Jakarta Sans',
                            cursor: 'default',
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M9.16667 6.83333H4.5M12.6667 6.83333C12.6667 10.055 10.055 12.6667 6.83333 12.6667C3.61167 12.6667 1 10.055 1 6.83333C1 3.61167 3.61167 1 6.83333 1C10.055 1 12.6667 3.61167 12.6667 6.83333Z" stroke="#E33B32" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          {badge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer d-flex">
          <button className="modal-cancel-btn" onClick={onClose} disabled={isSubmitting}>
            {t('CANCEL') || 'Cancel'}
          </button>
          <button className="modal-submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <svg className="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }}>
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.4 31.4" strokeDashoffset="0">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 8 8"
                      to="360 8 8"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
                {t('SUBMITTING') || 'Submitting...'}
              </>
            ) : (
              t('SUBMIT') || 'Submit'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceByTimesheetModal;
